<script>
	import { Router, Route, Link } from "svelte-routing";
	import { onMount } from "svelte";
	import Login from "./pages/LoginPage.svelte";
	import SignupPage from "./pages/SignupPage.svelte";
	import VerifyAccountPage from "./pages/VerifyAccountPage.svelte";
	import RequestPasswordResetPage from "./pages/RequestPasswordResetPage.svelte";
	import ResetPasswordPage from "./pages/ResetPasswordPage.svelte";
	import Dashboard from "./pages/Dashboard.svelte";
	import ProcessSelectedInvoices from "./pages/ProcessSelectedInvoices.svelte";
	import ImportBillsFromXero from "./pages/ImportBillsFromXero.svelte";
	import SyncSelectedBillsfromXero from "./pages/SyncSelectedBillsfromXero.svelte";
	import VendorBillDetails from "./pages/VendorBillDetails.svelte";
	import SyncBillsFromJobTread from "./pages/SyncBillsFromJobTread.svelte";
	import ImportFromJobTread from "./pages/ImportFromJobTread.svelte";
	import SyncPurchaseOrders from './pages/SyncPurchaseOrders.svelte';
	import SyncProgress from "./pages/SyncProgress.svelte";
	import SyncInvoices from "./pages/SyncInvoices.svelte";
	import SyncInvoicesProgress from "./pages/SyncInvoicesProgress.svelte";
	import ManageUsers from "./pages/ManageUsers.svelte";
	import SettingsXeroMapping from "./pages/SettingsXeroMapping.svelte";
	import SettingsSubscription from "./pages/settings/SettingsSubscription.svelte";
	import SettingsAccountingPlatform from "./pages/settings/SettingsAccountingPlatform.svelte";
	import SettingsQuickBooks from "./pages/settings/SettingsQuickBooks.svelte";
	import SettingsJobtread from "./pages/settings/SettingsJobtread.svelte";
	import SettingsXero from "./pages/settings/SettingsXero.svelte";
	import SelectXeroOrg from "./pages/SelectXeroOrg.svelte";
	import SettingsAutoSync from "./pages/settings/SettingsAutoSync.svelte";
	import SettingsInvoice from "./pages/settings/SettingsInvoice.svelte";
	import SettingsBillSync from "./pages/settings/SettingsBillSync.svelte";
	import SettingsApprovals from "./pages/settings/SettingsApprovals.svelte";
	import SettingsCostReporting from "./pages/settings/SettingsCostReporting.svelte";
	import SettingsOrganisation from "./pages/settings/SettingsOrganisation.svelte";
	import SettingsRedirect from "./pages/settings/SettingsRedirect.svelte";
	import HelpIndex from "./pages/HelpIndex.svelte";
	import HelpCategory from "./pages/HelpCategory.svelte";
	import HelpDoc from "./pages/HelpDoc.svelte";
	import DirectBillImport from "./pages/DirectBillImport.svelte";
	import ReviewDirectBills from "./pages/ReviewDirectBills.svelte";
	import Approvals from "./pages/Approvals.svelte";
	import CostReporting from "./pages/CostReporting.svelte";
	import CostReportDetail from "./pages/CostReportDetail.svelte";
	import CrmPipeline from "./pages/CrmPipeline.svelte";
	import AdminHelpVideos from "./pages/AdminHelpVideos.svelte";
	import PricingPage from "./pages/PricingPage.svelte";
	import { startVersionMonitoring } from "./utils/versionCheck";
	import { installFetchInterceptor } from "./utils/fetchInterceptor";
	import SubscriptionRequiredModal from "./components/SubscriptionRequiredModal.svelte";
	import { user, pendingWebhookEvents, autoSyncOnLogin } from "./stores/authStore";

	export let url = "";

	async function checkAuth() {
		try {
			const response = await fetch('/api/user', {
				credentials: 'include'
			});
			
			if (response.ok) {
				const data = await response.json();
				if (data.success && data.user) {
					user.set(data.user);
					
				// Update pending webhook events count
				if (typeof data.pendingWebhookEvents === 'number') {
					console.log('📊 Setting pending webhook events:', data.pendingWebhookEvents);
					pendingWebhookEvents.set(data.pendingWebhookEvents);
				}

				// Update auto-sync preference from user record
				if (typeof data.user.autoSyncOnLogin === 'boolean') {
					autoSyncOnLogin.set(data.user.autoSyncOnLogin);
				}
				}
			}
		} catch (error) {
			console.error('Error checking auth:', error);
			// Silently fail - user will need to log in
		}
	}

	onMount(() => {
		installFetchInterceptor();

		// Check authentication state and load pending webhook events
		checkAuth();
		
		// Start checking for new versions every 5 minutes
		startVersionMonitoring(5);
	});
</script>

<SubscriptionRequiredModal />

<Router {url}>
	<main>
		<Route path="/login" component={Login} />
		<Route path="/signup" component={SignupPage} />
		<Route path="/verify-account" component={VerifyAccountPage} />
		<Route path="/request-password-reset" component={RequestPasswordResetPage} />
		<Route path="/reset-password" component={ResetPasswordPage} />
		<Route path="/pricing" component={PricingPage} />
		<Route path="/dashboard" component={Dashboard} />
			
		<Route path="/process-selected-invoices" component={ProcessSelectedInvoices} />
		<Route path="/import-bills-from-xero" component={ImportBillsFromXero} />
		<Route path="/sync-selected-bills-from-xero" component={SyncSelectedBillsfromXero} />
		<Route path="/vendor-bill-details" component={VendorBillDetails} />
		<Route path="/sync-bills-from-jobtread" component={SyncBillsFromJobTread} />
		<Route path="/import-from-jobtread" component={ImportFromJobTread} />
		<Route path="/direct-bill-import" component={DirectBillImport} />
		<Route path="/review-direct-bills" component={ReviewDirectBills} />
		<Route path="/approvals" component={Approvals} />
		<Route path="/sync-pos" component={SyncPurchaseOrders} />
		<Route path="/sync-progress" component={SyncProgress} />
		<Route path="/sync-invoices" component={SyncInvoices} />
		<Route path="/sync-invoices-progress" component={SyncInvoicesProgress} />
		<Route path="/manage-users" component={ManageUsers} />
		<!-- Legacy path — Organisation now lives under Settings -->
		<Route path="/organisation" component={SettingsOrganisation} />
		<Route path="/cost-reporting/:jobId" let:params><CostReportDetail jobId={params.jobId} /></Route>
		<Route path="/cost-reporting" component={CostReporting} />
		<Route path="/crm" component={CrmPipeline} />
		<Route path="/admin/help-videos" component={AdminHelpVideos} />
		<Route path="/settings/subscription" component={SettingsSubscription} />
		<Route path="/settings/accounting-platform" component={SettingsAccountingPlatform} />
		<Route path="/settings/jobtread" component={SettingsJobtread} />
		<Route path="/select-xero-org" component={SelectXeroOrg} />
		<Route path="/settings/xero" component={SettingsXero} />
		<Route path="/settings/quickbooks" component={SettingsQuickBooks} />
		<Route path="/settings/auto-sync" component={SettingsAutoSync} />
		<Route path="/settings/invoice-settings" component={SettingsInvoice} />
		<Route path="/settings/bill-sync" component={SettingsBillSync} />
		<Route path="/settings/approvals" component={SettingsApprovals} />
		<Route path="/settings/cost-reporting" component={SettingsCostReporting} />
		<Route path="/settings/organisation" component={SettingsOrganisation} />
		<Route path="/settings/xero-mapping" component={SettingsXeroMapping} />
		<Route path="/settings" component={SettingsRedirect} />
		<Route path="/help" component={HelpIndex} />
		<Route path="/help/:category/:slug" component={HelpDoc} let:params />
		<Route path="/help/:category" component={HelpCategory} let:params />
		<Route path="/" component={Login} />
	</main>
</Router>

